import { CdkVirtualScrollViewport, CdkFixedSizeVirtualScroll, CdkVirtualForOf } from "@angular/cdk/scrolling";
import { HttpErrorResponse } from "@angular/common/http";
import { ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import {ActivatedRoute} from "@angular/router";
import { firstValueFrom, Subscription } from "rxjs";
import {FE_Sticker, FE_UserStickerPack,} from "@shared/models/integration";
import {WIDGET_STICKER_PICKER} from "@shared/models/widget";
import {StickerApiService} from "@shared/services/integrations/sticker-api.service";
import {MediaService} from "@shared/services/media.service";
import {ScalarServerApiService} from "@shared/services/scalar/scalar-server-api.service";
import {ScalarWidgetApi} from "@shared/services/scalar/scalar-widget.api";
import {SessionStorage} from "@shared/SessionStorage";
import {CapableWidget, WIDGET_API_VERSION_OPENID} from "../capable-widget";
import {NgxIndexedDBService} from 'ngx-indexed-db';
import {LogService} from "@shared/services/log.service";

import { SpinnerComponent } from "../../elements/spinner/spinner.component";
import { FormsModule } from "@angular/forms";
import { MatTabGroup, MatTab, MatTabLabel, MatTabContent } from "@angular/material/tabs";
import { LottieComponent } from "ngx-lottie";
import { TranslatePipe } from "@ngx-translate/core";

@Component({
    selector: "my-generic-widget-wrapper",
    templateUrl: "sticker-picker.component.html",
    styleUrls: ["sticker-picker.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SpinnerComponent, FormsModule, MatTabGroup, MatTab, MatTabLabel, LottieComponent, MatTabContent, CdkVirtualScrollViewport, CdkFixedSizeVirtualScroll, CdkVirtualForOf, TranslatePipe]
})
export class StickerPickerWidgetWrapperComponent
    extends CapableWidget
    implements OnInit, OnDestroy {
    public isLoading = true;
    public authError = false;
    public packs: any[];
    public isSearchSticker = false;
    public inputSearchSticker = '';
    public packsOfSearching: FE_UserStickerPack[];
    public isDesktop = false;
    private stickerWidgetApiSubscription: Subscription;

    @ViewChild('stickerPack') stickerPack: CdkVirtualScrollViewport;

    selectedIndex = 0;
    private touchStartX = 0;

    public previewSticker: any = null;
    public previewImageData: string | ArrayBuffer | null = null;
    private holdTimer: ReturnType<typeof setTimeout> | null = null;
    private holdTriggered = false;
    private holdStartX = 0;
    private holdStartY = 0;
    private static readonly HOLD_DELAY_MS = 1000;
    private static readonly HOLD_MOVE_CANCEL_PX = 10;
    private static readonly PREVIEW_CACHE_MAX_SIZE = 20;
    private readonly previewImageCache = new Map<string, Promise<string | ArrayBuffer>>();

    constructor(
        activatedRoute: ActivatedRoute,
        private readonly media: MediaService,
        private readonly scalarApi: ScalarServerApiService,
        private readonly stickerApi: StickerApiService,
        private readonly changeDetector: ChangeDetectorRef,
        private readonly dbService: NgxIndexedDBService,
        private readonly logService: LogService
    ) {
        super();
        this.supportsStickers = true;

        const params: any = activatedRoute.snapshot.queryParams;
        const userId = params.user_id;
        if (userId) {
            SessionStorage.userId = userId;
        }

        this.isDesktop = params.deviceType && params.deviceType === 'desktop';

        let token = params.scalar_token;
        if (token) {
            localStorage.setItem("dim-scalar-token", token);
        } else {
            token = localStorage.getItem("dim-scalar-token");
        }

        if (params.widgetId) {
            ScalarWidgetApi.widgetId = params.widgetId;
        } else {
            this.logService.error("No widgetId query parameter");
            this.authError = true;
            this.isLoading = false;
        }

        if (!this.authError) {
            SessionStorage.scalarToken = token;
            this.authError = !token;
            this.isLoading = !this.authError;
        }
    }

    public ngOnInit() {
        super.ngOnInit();
        this.stickerWidgetApiSubscription =
            ScalarWidgetApi.requestReceived.subscribe((request) => {
                if (request.action === "visibility") {
                    if (request.visible) {
                        this.loadStickers().then();
                    }
                    ScalarWidgetApi.replyAcknowledge(request);
                } else if (request.action === "clear_local_storage") {
                    localStorage.clear()
                }
            });
        this.loadStickers().then();
    }

    public ngOnDestroy() {
        super.ngOnDestroy();
        if (this.stickerWidgetApiSubscription)
            this.stickerWidgetApiSubscription.unsubscribe();
        this.clearHoldTimer();
        this.previewImageCache.clear();
    }

    protected onSupportedVersionsFound(isReload = true): void {
        super.onSupportedVersionsFound();

        if (
            this.authError &&
      this.doesSupportAtLeastVersion(WIDGET_API_VERSION_OPENID)
        ) {
            this.isLoading = true;
            this.changeDetector.detectChanges();

            this.getOpenIdInfo().then(async (response) => {
                if (response.blocked) {
                    this.isLoading = false;
                    this.authError = true;
                    if (isReload) {
                        this.onSupportedVersionsFound(false);
                    }
                    this.changeDetector.detectChanges();
                    return;
                }

                try {
                    const registerResponse = await this.scalarApi.register(
                        response.openId
                    );
                    localStorage.setItem(
                        "dim-scalar-token",
                        registerResponse.scalar_token
                    );
                    SessionStorage.scalarToken = registerResponse.scalar_token;
                    this.authError = !SessionStorage.scalarToken;
                    this.isLoading = false;
                    this.loadStickers().then();
                } catch (e) {
                    this.logService.error(e);
                    this.isLoading = false;
                    this.authError = true;
                }

                this.changeDetector.detectChanges();
            });
        }
    }

    public getThumbnailUrl(
        mxc: string,
        width: number,
        height: number,
        method: "crop" | "scale" = "scale"
    ): string {
        return this.media.getThumbnailUrl(mxc, width, height, method, true);
    }

    trackById(_: number, pack: any) {
        return pack.id;
    }
    private retryLoadStickers() {
        this.isLoading = true;
        this.changeDetector.detectChanges();
        this.getOpenIdInfo().then(async (response) => {
            if (response.blocked) {
                this.isLoading = false;
                this.authError = true;
                this.changeDetector.detectChanges();
                return;
            }

            try {
                const registerResponse = await this.scalarApi.register(
                    response.openId
                );
                localStorage.setItem(
                    "dim-scalar-token",
                    registerResponse.scalar_token
                );
                SessionStorage.scalarToken = registerResponse.scalar_token;
                this.authError = !SessionStorage.scalarToken;
                this.isLoading = false;
                this.loadStickers(false).then();
            } catch (e) {
                this.logService.error(e.message);
                this.isLoading = false;
                this.authError = true;
            }

            this.changeDetector.detectChanges();
        });
    }

    private async loadStickers(useCached = true): Promise<void | null> {
        this.isLoading = true;
        if (this.authError) return;
        if (!await this.resolveUserId()) return;
        if (!await this.loadPacksFromCacheOrApi(useCached)) return;
        await this.loadPackAvatars();
        this.isLoading = false;
        this.authError = false;
        this.changeDetector.markForCheck();
        this.changeDetector.detectChanges();
        this.storeStickers().then();
    }

    private async resolveUserId(): Promise<boolean> {
        if (SessionStorage.userId) return true;
        try {
            const info = await this.scalarApi.getAccount();
            SessionStorage.userId = info.user_id;
            this.logService.debug("Dimension scalar_token belongs to " + SessionStorage.userId);
            return true;
        } catch (e) {
            this.authError = true;
            this.isLoading = false;
            if (e instanceof HttpErrorResponse && e.status === 401) this.retryLoadStickers();
            return false;
        }
    }

    private async loadPacksFromCacheOrApi(useCached: boolean): Promise<boolean> {
        if (useCached) {
            try {
                const cached = await firstValueFrom(this.dbService.getAll('packs'));
                if (cached.length) {
                    this.packs = cached;
                    this.isLoading = false;
                    return true;
                }
            } catch (e) {
                this.logService.error(e);
            } finally {
                this.isLoading = false;
            }
        }
        return this.loadPacksFromApi();
    }

    private async loadPacksFromApi(): Promise<boolean> {
        try {
            this.logService.debug("Attempting to load available stickers...");
            const packs = await this.stickerApi.getPacksByUserId(SessionStorage.userId);
            this.packs = packs.filter((p: any) => p.isSelected);
            await this.savePacks();
            return true;
        } catch (e) {
            this.authError = true;
            if (e instanceof HttpErrorResponse && e.status === 401) this.retryLoadStickers();
            this.logService.error(e);
            return false;
        } finally {
            this.isLoading = false;
        }
    }

    private async savePacks(): Promise<void> {
        try {
            await firstValueFrom(this.dbService.clear('packs'));
            await firstValueFrom(this.dbService.bulkAdd('packs', this.packs)).catch((e) => {
                this.logService.error(e);
            });
        } catch (e) {
            this.logService.error(e);
        }
    }

    private async loadPackAvatars(): Promise<void> {
        for (const pack of this.packs) {
            if (pack.animationOpts?.animationData) continue;
            const animationData = await this.loadPackAvatarData(pack);
            if (animationData !== null) pack.animationOpts = { animationData };
        }
    }

    private async loadPackAvatarData(pack: any): Promise<any> {
        if (pack.isAnimated) {
            return firstValueFrom(this.media.getMediaContent(pack.avatarUrl)).catch(() => null);
        }
        const thumbnailUrl = this.media.getThumbnailUrl(pack.avatarUrl, 30, 30, 'scale', false);
        return this.getImageBase64DataFromUrl(thumbnailUrl).catch(() => null);
    }

    public async storeStickers(): Promise<void> {
        let stickers = [];
        try {
            stickers = await firstValueFrom(this.dbService.getAll('stickers'));
        } catch (e) {
            this.logService.error(e);
        }
        for (const pack of this.packs) {
            for (const sticker of pack.stickers) {
                await this.processStickerForPack(sticker, pack, stickers);
                this.changeDetector.detectChanges();
            }
        }
    }

    private async processStickerForPack(sticker: any, pack: any, cachedStickers: any[]): Promise<void> {
        const stickerId = `${pack.id}-${sticker.id}`;
        const cached = cachedStickers.find((e: any) => e.id === stickerId);
        if (cached) {
            sticker.animationOpts = cached.animationOpts;
            return;
        }
        if (sticker.animationOpts?.animationData) return;
        const animationData = await this.loadStickerAnimationData(sticker);
        const animationOpts = { animationData, loop: false, autoplay: true };
        sticker.animationOpts = animationOpts;
        await this.cacheStickerAnimationOpts(stickerId, animationOpts, cachedStickers);
    }

    private async loadStickerAnimationData(sticker: any, size = 48): Promise<string | ArrayBuffer> {
        if (sticker.image.mimetype === 'image/tgs') {
            return firstValueFrom(this.media.getMediaContent(sticker.image.mxc));
        }
        const thumbnailUrl = this.media.getThumbnailUrl(sticker.image.mxc, size, size, 'scale', false);
        return this.getImageBase64DataFromUrl(thumbnailUrl);
    }

    private async cacheStickerAnimationOpts(stickerId: string, animationOpts: any, cachedStickers: any[]): Promise<void> {
        try {
            await firstValueFrom(this.dbService.bulkAdd('stickers', [{ id: stickerId, animationOpts }]));
            cachedStickers.push({ id: stickerId, animationOpts });
            this.changeDetector.markForCheck();
            this.changeDetector.detectChanges();
        } catch (e) {
            this.logService.error(e);
        }
    }

    public scrollHorizontal(event: WheelEvent): void {
        // noinspection JSSuspiciousNameCombination
        document.getElementsByClassName("sticker-pack-list")[0].scrollLeft += event.deltaY;
        event.preventDefault();
    }

    public sendSticker(sticker: FE_Sticker, pack: FE_UserStickerPack) {
        if (this.holdTriggered) {
            // The click following a long-press preview shouldn't also send the sticker.
            this.holdTriggered = false;
            return;
        }
        ScalarWidgetApi.sendSticker(sticker, pack);
    }

    public onStickerPressStart(sticker: FE_Sticker, event: PointerEvent): void {
        this.clearHoldTimer();
        this.holdStartX = event.clientX;
        this.holdStartY = event.clientY;
        // Capturing the pointer keeps pointerup/pointercancel/pointermove targeted at this
        // element (and thus still bubbling to document) even if the pointer is dragged outside
        // this widget's iframe, so the preview can't get stuck open when release happens off-frame.
        (event.currentTarget as Element)?.setPointerCapture?.(event.pointerId);
        this.holdTimer = setTimeout(() => {
            this.holdTimer = null;
            this.holdTriggered = true;
            this.previewSticker = sticker;
            this.previewImageData = null;
            this.changeDetector.detectChanges();

            if (sticker.image.mimetype !== 'image/tgs') {
                this.loadPreviewImage(sticker).then((data) => {
                    // Ignore the result if the preview was dismissed (or swapped) before this resolved.
                    if (this.previewSticker !== sticker) return;
                    this.previewImageData = data;
                    this.changeDetector.detectChanges();
                }).catch((e) => this.logService.error(e));
            }
        }, StickerPickerWidgetWrapperComponent.HOLD_DELAY_MS);
    }

    public onStickerPressEnd(): void {
        this.clearHoldTimer();
        if (this.holdTriggered) {
            // The click that follows a long-press release on the same element needs to see
            // holdTriggered still true so it can suppress itself. If release happens off the
            // sticker (drag-off), no such click ever fires, so self-clear shortly after instead
            // of leaving the flag stuck and silently swallowing the next unrelated tap.
            setTimeout(() => {
                this.holdTriggered = false;
            }, 0);
        }
        if (this.previewSticker !== null) {
            this.previewSticker = null;
            this.previewImageData = null;
            this.changeDetector.detectChanges();
        }
    }

    private async loadPreviewImage(sticker: FE_Sticker): Promise<string | ArrayBuffer> {
        const cacheKey = sticker.image.mxc;
        const cached = await this.previewImageCache.get(cacheKey);
        if (cached) return cached;

        const thumbnailUrl = this.media.getThumbnailUrl(sticker.image.mxc, 200, 200, 'scale', false);
        const promise = this.getImageBase64DataFromUrl(thumbnailUrl);
        if (this.previewImageCache.size >= StickerPickerWidgetWrapperComponent.PREVIEW_CACHE_MAX_SIZE) {
            const oldestKey = this.previewImageCache.keys().next().value;
            this.previewImageCache.delete(oldestKey);
        }
        this.previewImageCache.set(cacheKey, promise);
        promise.catch(() => this.previewImageCache.delete(cacheKey));
        return promise;
    }

    private clearHoldTimer(): void {
        if (this.holdTimer !== null) {
            clearTimeout(this.holdTimer);
            this.holdTimer = null;
        }
    }

    @HostListener('document:pointerup')
    @HostListener('document:pointercancel')
    public onGlobalPointerRelease(): void {
        this.onStickerPressEnd();
    }

    @HostListener('document:pointermove', ['$event'])
    public onGlobalPointerMove(event: PointerEvent): void {
        // Only relevant while a hold is pending: a scroll/drag gesture that moves far enough
        // before HOLD_DELAY_MS elapses should cancel the pending long-press instead of
        // popping the preview open mid-scroll.
        if (this.holdTimer === null) return;
        const dx = event.clientX - this.holdStartX;
        const dy = event.clientY - this.holdStartY;
        if ((dx * dx + dy * dy) > (StickerPickerWidgetWrapperComponent.HOLD_MOVE_CANCEL_PX ** 2)) {
            this.clearHoldTimer();
        }
    }

    public openIntegrationManager() {
        ScalarWidgetApi.openIntegrationManager(
            WIDGET_STICKER_PICKER[0],
            ScalarWidgetApi.widgetId
        );
    }

    public refresh() {
        return this.loadStickers(false);
    }

    public searchStickers(searchText: string){
        this.packsOfSearching = [];
        if(searchText.trim() === ''){
            return;
        }
        this.packs.forEach((pack) => {
            const stickers = pack.stickers.filter((sticker: FE_Sticker) => {
                return sticker.name.toUpperCase().includes(searchText.toUpperCase());
            });
            if(stickers.length){
                this.packsOfSearching.push({
                    ...pack,
                    stickers
                });
            }
        });
    }

    public closeInputSearchStickers(){
        this.isSearchSticker = !this.isSearchSticker;
        this.inputSearchSticker = '';
        this.packsOfSearching = [];
    }

    public onTouchStart(event: TouchEvent) {
        this.touchStartX = event.touches[0].clientX;
    }

    public onTouchEnd(event: TouchEvent) {
        const deltaX = event.changedTouches[0].clientX - this.touchStartX;
        const SWIPE_THRESHOLD = 50;
        if (Math.abs(deltaX) < SWIPE_THRESHOLD) return;

        const tabCount = (this.inputSearchSticker.trim() === '' ? this.packs.length : this.packsOfSearching.length) - 1;
        if (this.selectedIndex < 0) {
            this.selectedIndex = 0; return;
        }
        if (this.selectedIndex > tabCount) {
            this.selectedIndex = tabCount; return;
        }

        if (deltaX < 0) {
            // Swipe left → next tab
            this.selectedIndex = this.selectedIndex === tabCount ? tabCount : this.selectedIndex + 1;
        } else {
            // Swipe right → previous tab
            this.selectedIndex = this.selectedIndex === 0 ? 0 : this.selectedIndex - 1;
        }
    }

    private async getImageBase64DataFromUrl(imageUrl: string): Promise<string | ArrayBuffer> {
        const res = await fetch(imageUrl);
        const blob = await res.blob();

        return new Promise((resolve, reject) => {
            const reader  = new FileReader();
            reader.addEventListener("load", function () {
                resolve(reader.result);
            }, false);

            reader.onerror = () => {
                return reject(new Error(`Failed to read as data URL of ${imageUrl}`));
            };
            reader.readAsDataURL(blob);
        })
    }
}
